// services/greetingCardService.js - Greeting Card Service for Web
import supabase from './supabase';

// Compress image for web using browser's canvas API
const compressImage = async (file, maxWidth = 1200) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            }));
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        file.type,
        0.85
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

// Generate unique slug for card
export const generateUniqueCardSlug = async (title) => {
  const baseSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);

  let slug = baseSlug;
  let counter = 1;

  while (true) {
    const { data, error } = await supabase
      .from('card_slugs')
      .select('slug')
      .eq('slug', slug)
      .single();

    if (error && error.code === 'PGRST116') {
      // Slug doesn't exist, it's unique
      return slug;
    }

    if (error) {
      throw error;
    }

    // Slug exists, try with counter
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
};

// Upload card cover image (handles images and GIFs)
export const uploadCardCoverImage = async (file, cardId) => {
  try {
    const isGif = file.type === 'image/gif';

    if (isGif) {
      // For GIFs, upload both animated and static version
      return await uploadCardGif(file, cardId);
    }

    // Compress regular image
    const compressedFile = await compressImage(file, 1200);

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${cardId}-${Date.now()}.${fileExt}`;
    const filePath = `card-covers/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('event-images')
      .upload(filePath, compressedFile, {
        contentType: file.type,
        upsert: false
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(filePath);

    return {
      cdnUrl: publicUrl,
      path: filePath,
      type: 'image'
    };
  } catch (error) {
    console.error('Error uploading card cover image:', error);
    throw error;
  }
};

// Upload GIF (animated + static version)
export const uploadCardGif = async (file, cardId) => {
  try {
    // Upload animated GIF
    const gifFileName = `${cardId}-${Date.now()}.gif`;
    const gifPath = `card-covers/${gifFileName}`;

    const { data: gifData, error: gifError } = await supabase.storage
      .from('event-images')
      .upload(gifPath, file, {
        contentType: 'image/gif',
        upsert: false
      });

    if (gifError) throw gifError;

    const { data: { publicUrl: gifUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(gifPath);

    // Create static version (first frame)
    const staticFile = await gifToStaticImage(file);
    const staticFileName = `${cardId}-${Date.now()}-static.jpg`;
    const staticPath = `card-covers/${staticFileName}`;

    const { data: staticData, error: staticError } = await supabase.storage
      .from('event-images')
      .upload(staticPath, staticFile, {
        contentType: 'image/jpeg',
        upsert: false
      });

    if (staticError) throw staticError;

    const { data: { publicUrl: staticUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(staticPath);

    return {
      type: 'gif',
      gifCdnUrl: gifUrl,
      gifPath: gifPath,
      staticCdnUrl: staticUrl,
      staticPath: staticPath,
      cdnUrl: gifUrl, // Use animated as primary
      path: gifPath
    };
  } catch (error) {
    console.error('Error uploading GIF:', error);
    throw error;
  }
};

// Convert GIF to static image (first frame)
const gifToStaticImage = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], 'static.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            }));
          } else {
            reject(new Error('Failed to create static image'));
          }
        },
        'image/jpeg',
        0.85
      );
    };

    img.onerror = () => reject(new Error('Failed to load GIF'));
    reader.onerror = () => reject(new Error('Failed to read GIF file'));
    reader.readAsDataURL(file);
  });
};

// Create new greeting card
export const createGreetingCard = async (cardData) => {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in to create a greeting card');
    }

    // Generate unique slug
    const slug = await generateUniqueCardSlug(cardData.title);

    // Prepare card data
    const newCard = {
      creator_id: user.id,
      title: cardData.title,
      notes: cardData.notes || '',
      cover_image_type: cardData.cover_image_type || 'image',
      cover_image_cdn_url: cardData.cover_image_cdn_url,
      cover_thumbnail_cdn_url: cardData.cover_thumbnail_cdn_url || cardData.cover_image_cdn_url,
      theme_type: cardData.theme_type || 'color',
      theme_color: cardData.theme_color,
      theme_image_cdn_url: cardData.theme_image_cdn_url,
      theme_video_cdn_url: cardData.theme_video_cdn_url,
      theme_thumbnail_cdn_url: cardData.theme_thumbnail_cdn_url,
      card_style: cardData.card_style || 'default',
      font_family: cardData.font_family || 'default',
      envelope_style: cardData.envelope_style || 'classic',
      privacy: cardData.privacy || 'public',
      guest_list_ids: cardData.guest_list_ids || [],
      shareable_slug: slug,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Insert card
    const { data: card, error: insertError } = await supabase
      .from('greeting_cards')
      .insert([newCard])
      .select()
      .single();

    if (insertError) throw insertError;

    // Insert slug mapping
    const { error: slugError } = await supabase
      .from('card_slugs')
      .insert([{
        card_id: card.id,
        slug: slug
      }]);

    if (slugError) {
      console.error('Error inserting slug:', slugError);
      // Continue anyway as card is created
    }

    console.log('✅ Greeting card created:', card.id);
    return { card, slug };
  } catch (error) {
    console.error('❌ Error creating greeting card:', error);
    throw error;
  }
};

// Update greeting card
export const updateGreetingCard = async (cardId, updates) => {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in to update a greeting card');
    }

    // Verify ownership
    const { data: card, error: fetchError } = await supabase
      .from('greeting_cards')
      .select('creator_id')
      .eq('id', cardId)
      .single();

    if (fetchError) throw fetchError;
    if (card.creator_id !== user.id) {
      throw new Error('You can only update your own greeting cards');
    }

    // Update card
    const { data: updatedCard, error: updateError } = await supabase
      .from('greeting_cards')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', cardId)
      .select()
      .single();

    if (updateError) throw updateError;

    console.log('✅ Greeting card updated:', cardId);
    return updatedCard;
  } catch (error) {
    console.error('❌ Error updating greeting card:', error);
    throw error;
  }
};

// Get greeting card by ID
export const getGreetingCardById = async (cardId) => {
  try {
    const { data: card, error } = await supabase
      .from('greeting_cards')
      .select('*')
      .eq('id', cardId)
      .single();

    if (error) throw error;

    return card;
  } catch (error) {
    console.error('❌ Error fetching greeting card:', error);
    throw error;
  }
};

// Get greeting card by slug
export const getGreetingCardBySlug = async (slug) => {
  try {
    // Get card ID from slug
    const { data: slugData, error: slugError } = await supabase
      .from('card_slugs')
      .select('card_id')
      .eq('slug', slug)
      .single();

    if (slugError) {
      if (slugError.code === 'PGRST116') {
        return { card: null, accessDenied: false };
      }
      throw slugError;
    }

    // Get card data
    const { data: card, error: cardError } = await supabase
      .from('greeting_cards')
      .select('*')
      .eq('id', slugData.card_id)
      .single();

    if (cardError) throw cardError;

    // Check access permissions
    const canAccess = await canAccessGreetingCard(card);

    if (!canAccess) {
      return { card: null, accessDenied: true };
    }

    return { card, accessDenied: false };
  } catch (error) {
    console.error('❌ Error fetching greeting card by slug:', error);
    throw error;
  }
};

// Check if current user can access card
export const canAccessGreetingCard = async (card) => {
  if (!card) return false;

  // Public cards are accessible to everyone
  if (card.privacy === 'public') return true;

  // Get current user
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) return false;

  // Creator can always access
  if (card.creator_id === user.id) return true;

  // Check if user is in guest list
  if (card.guest_list_ids && Array.isArray(card.guest_list_ids)) {
    return card.guest_list_ids.includes(user.id);
  }

  return false;
};

// Get user's greeting cards
export const getUserGreetingCards = async (userId = null) => {
  try {
    let targetUserId = userId;

    // If no userId provided, get current user
    if (!targetUserId) {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to view your greeting cards');
      }
      targetUserId = user.id;
    }

    const { data: cards, error } = await supabase
      .from('greeting_cards')
      .select('*')
      .eq('creator_id', targetUserId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return cards || [];
  } catch (error) {
    console.error('❌ Error fetching user greeting cards:', error);
    throw error;
  }
};

// Delete greeting card
export const deleteGreetingCard = async (cardId) => {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in to delete a greeting card');
    }

    // Verify ownership
    const { data: card, error: fetchError } = await supabase
      .from('greeting_cards')
      .select('creator_id, cover_image_path')
      .eq('id', cardId)
      .single();

    if (fetchError) throw fetchError;
    if (card.creator_id !== user.id) {
      throw new Error('You can only delete your own greeting cards');
    }

    // Delete cover image from storage if exists
    if (card.cover_image_path) {
      const { error: storageError } = await supabase.storage
        .from('event-images')
        .remove([card.cover_image_path]);

      if (storageError) {
        console.error('Error deleting cover image:', storageError);
      }
    }

    // Delete card slugs
    const { error: slugError } = await supabase
      .from('card_slugs')
      .delete()
      .eq('card_id', cardId);

    if (slugError) {
      console.error('Error deleting card slugs:', slugError);
    }

    // Delete card
    const { error: deleteError } = await supabase
      .from('greeting_cards')
      .delete()
      .eq('id', cardId);

    if (deleteError) throw deleteError;

    console.log('✅ Greeting card deleted:', cardId);
    return true;
  } catch (error) {
    console.error('❌ Error deleting greeting card:', error);
    throw error;
  }
};

// Add guests to private card
export const addGuestsToCard = async (cardId, guestUserIds) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in');
    }

    // Get current card
    const card = await getGreetingCardById(cardId);
    if (card.creator_id !== user.id) {
      throw new Error('Only the creator can add guests');
    }

    // Merge guest lists (avoid duplicates)
    const currentGuests = card.guest_list_ids || [];
    const newGuests = [...new Set([...currentGuests, ...guestUserIds])];

    // Update card
    await updateGreetingCard(cardId, {
      guest_list_ids: newGuests
    });

    return true;
  } catch (error) {
    console.error('Error adding guests to card:', error);
    throw error;
  }
};

// Remove guests from private card
export const removeGuestsFromCard = async (cardId, guestUserIds) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in');
    }

    // Get current card
    const card = await getGreetingCardById(cardId);
    if (card.creator_id !== user.id) {
      throw new Error('Only the creator can remove guests');
    }

    // Remove guests
    const currentGuests = card.guest_list_ids || [];
    const updatedGuests = currentGuests.filter(id => !guestUserIds.includes(id));

    // Update card
    await updateGreetingCard(cardId, {
      guest_list_ids: updatedGuests
    });

    return true;
  } catch (error) {
    console.error('Error removing guests from card:', error);
    throw error;
  }
};

export default {
  createGreetingCard,
  updateGreetingCard,
  getGreetingCardById,
  getGreetingCardBySlug,
  getUserGreetingCards,
  deleteGreetingCard,
  uploadCardCoverImage,
  generateUniqueCardSlug,
  canAccessGreetingCard,
  addGuestsToCard,
  removeGuestsFromCard
};
